import { useEffect, useState } from "react";
import { Shield, SlidersHorizontal, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { LEGAL_COMPANY_INFO } from "./legalConfig";

export const COOKIE_CONSENT_STORAGE_KEY = "dotus_crm_cookie_consent_v1";
const OPEN_PREFERENCES_EVENT = "dotus:open-cookie-preferences";
export const CONSENT_UPDATED_EVENT = "dotus:cookie-consent-updated";

export interface CookieConsentPreferences {
  necessary: true;
  analytics: boolean;
  marketing: boolean;
  decidedAt: string;
  version: string;
}

export function getCookieConsent(): CookieConsentPreferences | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(COOKIE_CONSENT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    return {
      necessary: true,
      analytics: Boolean(parsed.analytics),
      marketing: Boolean(parsed.marketing),
      decidedAt: String(parsed.decidedAt || new Date().toISOString()),
      version: String(
        parsed.version || LEGAL_COMPANY_INFO.versaoPoliticaCookies,
      ),
    };
  } catch {
    return null;
  }
}

export function hasCookieConsent(
  category: "necessary" | "analytics" | "marketing",
): boolean {
  if (category === "necessary") return true;
  const current = getCookieConsent();
  if (!current) return false;
  return Boolean(current[category]);
}

export function saveCookieConsent(prefs: {
  analytics: boolean;
  marketing: boolean;
}): CookieConsentPreferences {
  const record: CookieConsentPreferences = {
    necessary: true,
    analytics: Boolean(prefs.analytics),
    marketing: Boolean(prefs.marketing),
    decidedAt: new Date().toISOString(),
    version: LEGAL_COMPANY_INFO.versaoPoliticaCookies,
  };
  if (typeof window !== "undefined") {
    try {
      window.localStorage.setItem(
        COOKIE_CONSENT_STORAGE_KEY,
        JSON.stringify(record),
      );
      window.dispatchEvent(
        new CustomEvent(CONSENT_UPDATED_EVENT, { detail: record }),
      );
    } catch {}
  }
  return record;
}

export function openCookiePreferences() {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(OPEN_PREFERENCES_EVENT));
  }
}

export const CookieConsentBanner = () => {
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    const existing = getCookieConsent();
    if (!existing) {
      setVisible(true);
    } else {
      setAnalytics(existing.analytics);
      setMarketing(existing.marketing);
    }

    const handleOpen = () => {
      const current = getCookieConsent();
      if (current) {
        setAnalytics(current.analytics);
        setMarketing(current.marketing);
      } else {
        setAnalytics(false);
        setMarketing(false);
      }
      setShowDetails(true);
      setVisible(true);
    };

    window.addEventListener(OPEN_PREFERENCES_EVENT, handleOpen);
    return () => window.removeEventListener(OPEN_PREFERENCES_EVENT, handleOpen);
  }, []);

  if (!visible) return null;

  const handleRejectAll = () => {
    saveCookieConsent({ analytics: false, marketing: false });
    setAnalytics(false);
    setMarketing(false);
    setVisible(false);
    setShowDetails(false);
  };

  const handleAcceptAll = () => {
    saveCookieConsent({ analytics: true, marketing: true });
    setAnalytics(true);
    setMarketing(true);
    setVisible(false);
    setShowDetails(false);
  };

  const handleSaveCustom = () => {
    saveCookieConsent({ analytics, marketing });
    setVisible(false);
    setShowDetails(false);
  };

  return (
    <section
      role="region"
      aria-label="Aviso e Preferências de Cookies (LGPD)"
      className="fixed bottom-0 inset-x-0 z-50 p-3 sm:p-4 pointer-events-none flex justify-center"
    >
      <div className="pointer-events-auto w-full max-w-4xl bg-card/95 backdrop-blur-md border border-border rounded-xl shadow-2xl p-4 sm:p-5 text-card-foreground space-y-4">
        <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-lg bg-primary/10 text-primary shrink-0 mt-0.5">
              <Shield className="w-5 h-5" aria-hidden="true" />
            </div>
            <div className="space-y-1.5 text-xs sm:text-sm">
              <h2 className="font-semibold text-foreground text-sm sm:text-base">
                Sua privacidade e controle de cookies (LGPD)
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                Utilizamos cookies estritamente necessários para autenticação e
                segurança da sua sessão. Cookies opcionais de análise
                (analytics) e marketing permanecem{" "}
                <strong>desativados por padrão</strong> e só são carregados se
                você autorizar, conforme a Lei nº 13.709/2018 (LGPD) e o Guia
                Orientativo de Cookies da ANPD. Consulte nossa{" "}
                <a
                  href="#/cookies"
                  className="text-primary underline underline-offset-2 hover:opacity-80 font-medium"
                >
                  Política de Cookies
                </a>{" "}
                e{" "}
                <a
                  href="#/privacidade"
                  className="text-primary underline underline-offset-2 hover:opacity-80 font-medium"
                >
                  Política de Privacidade
                </a>
                .
              </p>
            </div>
          </div>

          <div className="flex flex-wrap sm:flex-nowrap items-center gap-2 shrink-0">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setShowDetails((prev) => !prev)}
              className="text-xs font-medium h-9 px-3 cursor-pointer"
              aria-expanded={showDetails}
            >
              <SlidersHorizontal
                className="w-3.5 h-3.5 mr-1.5"
                aria-hidden="true"
              />
              Personalizar
            </Button>

            {/* Botões "Rejeitar Tudo" e "Aceitar Tudo" com mesmo destaque visual e tamanho */}
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={handleRejectAll}
              className="text-xs font-semibold h-9 px-4 border border-border cursor-pointer"
            >
              Rejeitar opcionais
            </Button>

            <Button
              type="button"
              variant="default"
              size="sm"
              onClick={handleAcceptAll}
              className="text-xs font-semibold h-9 px-4 cursor-pointer"
            >
              Aceitar todos
            </Button>
          </div>
        </div>

        {showDetails && (
          <div className="border-t border-border pt-4 space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
              {/* Categoria 1: Necessários */}
              <div className="p-3 rounded-lg border border-border bg-muted/40 space-y-1">
                <div className="flex items-center justify-between font-semibold text-foreground">
                  <span>1. Estritamente Necessários</span>
                  <span className="text-[11px] px-2 py-0.5 rounded bg-primary/15 text-primary">
                    Sempre ativos
                  </span>
                </div>
                <p className="text-muted-foreground leading-snug">
                  Indispensáveis para login (Supabase Auth), sessão criptografada
                  de telefonia VoIP e proteção contra fraudes. Não armazenam
                  rastreamento publicitário.
                </p>
              </div>

              {/* Categoria 2: Analytics */}
              <label className="p-3 rounded-lg border border-border bg-background hover:bg-muted/20 transition-colors space-y-1 cursor-pointer flex flex-col justify-between">
                <div className="space-y-1">
                  <div className="flex items-center justify-between font-semibold text-foreground">
                    <span>2. Desempenho e Analytics</span>
                    <input
                      type="checkbox"
                      checked={analytics}
                      onChange={(e) => setAnalytics(e.target.checked)}
                      className="h-4 w-4 rounded border-border accent-primary cursor-pointer"
                    />
                  </div>
                  <p className="text-muted-foreground leading-snug">
                    Permitem medir estabilidade técnica e uso agregado da
                    plataforma para melhoria contínua. Desligados por padrão.
                  </p>
                </div>
              </label>

              {/* Categoria 3: Marketing */}
              <label className="p-3 rounded-lg border border-border bg-background hover:bg-muted/20 transition-colors space-y-1 cursor-pointer flex flex-col justify-between">
                <div className="space-y-1">
                  <div className="flex items-center justify-between font-semibold text-foreground">
                    <span>3. Marketing e Pixels</span>
                    <input
                      type="checkbox"
                      checked={marketing}
                      onChange={(e) => setMarketing(e.target.checked)}
                      className="h-4 w-4 rounded border-border accent-primary cursor-pointer"
                    />
                  </div>
                  <p className="text-muted-foreground leading-snug">
                    Utilizados para atribuição de campanhas e integrações de
                    anúncios quando aplicável. Desligados por padrão.
                  </p>
                </div>
              </label>
            </div>

            <div className="flex justify-end gap-2 pt-1">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleSaveCustom}
                className="text-xs font-semibold h-8 px-4 cursor-pointer"
              >
                <Check className="w-3.5 h-3.5 mr-1.5" aria-hidden="true" />
                Salvar minhas preferências
              </Button>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

